<template>
  <div>
    <v-container fluid>
      <v-toolbar color="primary" class="header-title text-white">
        <v-toolbar-title class="title">Tax History</v-toolbar-title>
      </v-toolbar>

      <!-- Loading -->
      <div v-if="state.detailLoading || fieldsLoading" class="pa-4">
        <v-progress-linear indeterminate color="primary" />
        <div class="text-center text-body-2 mt-3">กำลังโหลด...</div>
      </div>

      <!-- Not found (404) -->
      <div
        v-else-if="state.detailErrorStatus === 404"
        class="text-center pa-8"
        data-testid="detail-not-found"
      >
        <v-icon size="48" color="grey">mdi-file-remove-outline</v-icon>
        <div class="text-h6 mt-2">Form not found</div>
        <v-btn class="mt-4" color="primary" @click="$router.push('/TaxHistory')">Back to History</v-btn>
      </div>

      <!-- Other error -->
      <div v-else-if="state.detailError" class="pa-4">
        <v-alert type="error" variant="tonal" data-testid="detail-error">
          {{ state.detailError }}
          <template #append>
            <v-btn size="small" variant="text" @click="reload">ลองใหม่</v-btn>
          </template>
        </v-alert>
      </div>

      <!-- Loaded -->
      <template v-else-if="form">
        <div class="d-flex align-center mb-4 px-4 pt-4">
          <v-btn
            data-testid="detail-back-btn"
            variant="outlined"
            color="primary"
            size="large"
            rounded="lg"
            prepend-icon="mdi-arrow-left"
            @click="$router.push('/TaxHistory')"
          >
            Back
          </v-btn>
        </div>

        <!-- Record header: who / what state / when, at a glance -->
        <v-card class="card2 mb-4 record-header" data-testid="detail-form-info">
          <v-card-text>
            <div class="rh-top">
              <div class="rh-id">
                <h2 class="rh-title">Tax Form #{{ form.formID }}</h2>
                <div class="rh-cif">
                  <span class="rh-cif__label">CIF</span>
                  <CopyId :value="form.userID" label="CIF" />
                </div>
              </div>
              <v-chip :color="statusColor(form.status)" size="large" label class="rh-status">
                {{ form.status }}
              </v-chip>
            </div>

            <div class="rh-meta">
              <v-chip size="small" variant="tonal" label>
                <v-icon start size="16">mdi-calendar-outline</v-icon>
                ปีภาษี {{ form.taxYearCode || '—' }}
              </v-chip>
              <span class="rh-timeline">
                <v-icon size="16" class="mr-1">mdi-clock-start</v-icon>
                <span>{{ formatDate(form.startedAt) }}</span>
                <v-icon size="16" class="mx-2">mdi-arrow-right</v-icon>
                <span v-if="form.completedAt">{{ formatDate(form.completedAt) }}</span>
                <span v-else class="text-warning">กำลังดำเนินการ</span>
              </span>
            </div>

            <div v-if="form.totalRequired > 0" class="rh-progress">
              <div class="rh-progress__label">
                <span>ความคืบหน้า (Section)</span>
                <span>{{ form.answeredCount }} / {{ form.totalRequired }}</span>
              </div>
              <v-progress-linear
                :model-value="progressPct"
                :color="progressColor"
                height="6"
                rounded
              />
            </div>
          </v-card-text>
        </v-card>

        <!-- Captured Answers: the hero of the page, one table grouped by section -->
        <v-card class="card2 mb-4 answers-card" data-testid="detail-answers">
          <v-card-title class="d-flex align-center flex-wrap">
            <v-icon class="mr-2">mdi-format-list-checks</v-icon>
            Captured Answers
            <v-chip class="ml-2" size="small" variant="tonal">{{ answersCountLabel }}</v-chip>
          </v-card-title>
          <v-card-text>
            <div v-if="fields.length === 0" class="text-center text-body-2 text-medium-emphasis pa-6">
              ฟอร์มนี้ยังไม่มีคำถาม
            </div>

            <template v-else>
              <!-- Search + persistent jargon legend (headers keep their tooltips too) -->
              <div class="answers-toolbar">
                <v-text-field
                  v-model="answerSearch"
                  data-testid="detail-answer-search"
                  prepend-inner-icon="mdi-magnify"
                  label="ค้นหา คำถาม / คำตอบ / rule / field"
                  variant="outlined"
                  density="compact"
                  hide-details
                  clearable
                  class="answers-search"
                />
                <p class="answers-legend">
                  <strong>Rule · Field</strong> = กฎ/ฟิลด์ที่แมป ·
                  <strong>Kind</strong> = ประเภทคำถาม
                </p>
              </div>

              <div
                v-if="filteredGroups.length === 0"
                class="text-center text-body-2 text-medium-emphasis pa-6"
                data-testid="detail-answer-nomatch"
              >
                ไม่พบคำตอบที่ตรงกับ “{{ answerSearch }}”
              </div>

              <div v-else class="answers-scroll">
                <v-table density="comfortable" class="answers-table">
                  <thead>
                    <tr>
                      <th>คำถาม</th>
                      <template v-if="showPersonColumns">
                        <th class="answer-a">ผู้มีเงินได้ (คนที่ 1)</th>
                        <th class="answer-a">คู่สมรส (คนที่ 2)</th>
                      </template>
                      <th v-else>คำตอบ</th>
                      <th class="th-jargon">
                        Rule · Field
                        <v-tooltip text="กฎภาษี / คีย์ฟิลด์ที่คำตอบนี้ถูกแมปไป" location="top">
                          <template #activator="{ props }">
                            <v-icon v-bind="props" size="14" class="th-info" aria-label="คำอธิบาย Rule · Field">mdi-information-outline</v-icon>
                          </template>
                        </v-tooltip>
                      </th>
                      <th class="th-jargon">
                        Kind
                        <v-tooltip text="ประเภทของรายการคำถาม เช่น choice, number, text" location="top">
                          <template #activator="{ props }">
                            <v-icon v-bind="props" size="14" class="th-info" aria-label="คำอธิบาย Kind">mdi-information-outline</v-icon>
                          </template>
                        </v-tooltip>
                      </th>
                      <th class="text-no-wrap">ตอบเมื่อ</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="g in filteredGroups" :key="g.code || 'ungrouped'">
                      <tr class="group-row">
                        <th :colspan="answerColspan" class="group-row__cell">
                          <v-icon size="18" class="mr-2 text-medium-emphasis">mdi-tag-outline</v-icon>
                          <span class="group-row__name">{{ g.label || sectionLabel(g.code) }}</span>
                          <v-chip size="x-small" variant="tonal" class="ml-2">{{ g.items.length }}</v-chip>
                        </th>
                      </tr>
                      <tr v-for="a in g.items" :key="a.key">
                        <td class="answer-q">{{ a.questionTextTh || '—' }}</td>
                        <template v-if="showPersonColumns">
                          <td class="answer-a">
                            <v-chip v-if="isSkipped(a.answerP1)" size="x-small" color="warning" variant="tonal">ข้ามไว้</v-chip>
                            <span v-else-if="a.answerP1">{{ formatAnswer(a.answerP1) }}</span>
                            <span v-else class="text-disabled">ยังไม่ตอบ</span>
                          </td>
                          <td class="answer-a">
                            <v-chip v-if="isSkipped(a.answerP2)" size="x-small" color="warning" variant="tonal">ข้ามไว้</v-chip>
                            <span v-else-if="a.answerP2">{{ formatAnswer(a.answerP2) }}</span>
                            <span v-else class="text-disabled">ยังไม่ตอบ</span>
                          </td>
                        </template>
                        <td v-else class="answer-a">
                          <v-chip v-if="isSkipped(a.answer)" size="x-small" color="warning" variant="tonal">ข้ามไว้</v-chip>
                          <span v-else-if="a.answer">{{ answerText(a) }}</span>
                          <span v-else class="text-disabled">ยังไม่ตอบ</span>
                        </td>
                        <td class="text-no-wrap">{{ a.ruleCode || a.fieldKey || '—' }}</td>
                        <td>{{ a.itemKind || '—' }}</td>
                        <td class="text-no-wrap">{{ a.answer ? formatDate(a.answer.answeredAt) : '—' }}</td>
                      </tr>
                    </template>
                  </tbody>
                </v-table>
              </div>
            </template>
          </v-card-text>
        </v-card>

        <!-- Technical details: secondary, collapsed by default -->
        <v-expansion-panels class="tech-panels mb-6">
          <v-expansion-panel>
            <v-expansion-panel-title>
              <v-icon start size="18">mdi-wrench-outline</v-icon>
              Technical details
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <dl class="tech-list">
                <div class="tech-item">
                  <dt>Form ID</dt>
                  <dd><CopyId :value="form.formID" label="Form ID" /></dd>
                </div>
                <div class="tech-item">
                  <dt>Session</dt>
                  <dd><CopyId :value="form.sessionID" label="Session" truncate /></dd>
                </div>
                <div class="tech-item">
                  <dt>Channel</dt>
                  <dd>{{ form.channel || '—' }}</dd>
                </div>
                <div class="tech-item">
                  <dt>Current Group</dt>
                  <dd>{{ form.currentGroupCode || '—' }}</dd>
                </div>
                <div class="tech-item">
                  <dt>Config Version</dt>
                  <dd>{{ form.taxConfigVersionID ?? '—' }}</dd>
                </div>
                <div class="tech-item">
                  <dt>Question Version</dt>
                  <dd>{{ form.questionVersionID ?? '—' }}</dd>
                </div>
              </dl>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>
    </v-container>
  </div>
</template>

<script>
import { state, loadFormDetail, formatDate } from '../utils/taxHistoryStore.js';
// Canonical section labels (ข้อมูลส่วนตัว / รายได้ / ค่าลดหย่อน / ...) so the
// detail view names sections exactly like the question builder does.
import { groupLabel } from '../utils/taxQuestionStore.js';
// The form's own `answers` only cover questions the user has already
// answered — the full field list (answered or not) comes from the question
// version config, fetched separately and merged in `fields`.
import { taxQuestionApi } from '../services/taxQuestionApi.js';
import CopyId from '../components/CopyId.vue';

export default {
  name: 'TaxHistoryDetail',
  components: { CopyId },
  data() {
    return {
      // Client-side filter over the captured answers (question / answer / rule / field / kind).
      answerSearch: '',
      // Full question-item config (`{ version, steps, groups }`) for the form's
      // questionVersionID — every field, whether answered or not.
      questionFull: null,
      fieldsLoading: false,
    };
  },
  computed: {
    state() {
      return state;
    },
    form() {
      return state.detail?.form || null;
    },
    answers() {
      return state.detail?.answers || [];
    },
    // Joint filing (ยื่นร่วม): when the form is joint, the answer column splits
    // into person 1 (ผู้มีเงินได้) / person 2 (คู่สมรส). Non-joint forms collapse
    // to the single "คำตอบ" column exactly as before (default false).
    isJoint() {
      return !!(this.form && this.form.isJointFiling);
    },
    // Display gate for the person-1 / person-2 split. Show the two-column layout
    // when the form is flagged joint OR when ANY captured answer belongs to person 2
    // (personIndex ≥ 2) — so a spouse's answers are NEVER hidden even if the joint
    // flag lags or is missing. Non-joint forms with no person-2 data collapse to the
    // single "คำตอบ" column exactly as before.
    showPersonColumns() {
      return this.isJoint || this.answers.some((a) => (a.personIndex || 1) >= 2);
    },
    // Group subheader colspan: 5 base columns (คำถาม · คำตอบ · Rule·Field · Kind ·
    // ตอบเมื่อ), +1 when the split layout adds the second answer column.
    answerColspan() {
      return this.showPersonColumns ? 6 : 5;
    },
    // Completion percentage for the header progress bar. Guarded so an
    // InProgress form with 0 required questions never divides by zero.
    progressPct() {
      const f = this.form;
      if (!f || !f.totalRequired) return 0;
      return Math.min(100, Math.round((f.answeredCount / f.totalRequired) * 100));
    },
    // Bar color follows the form's STATUS, not the fill %: green only when actually Completed, so a
    // form that reached the last section (N/N) while still InProgress stays primary (blue) instead of a
    // misleading full-green bar. Abandoned reads grey. Kept a dedicated computed (rather than reusing the
    // status chip's amber for InProgress) so the in-progress fill stays a conventional progress blue.
    progressColor() {
      switch (this.form && this.form.status) {
        case 'Completed': return 'success';
        case 'Abandoned': return 'grey';
        default: return 'primary';
      }
    },
    // Every field configured on the form's question version, merged with its
    // captured answer when one exists — so the table shows the whole form
    // (answered or not) instead of only questions the user has already
    // answered. Falls back to answers-only if the question config didn't load
    // (e.g. still fetching, or the form has no questionVersionID).
    fields() {
      // Joint filing (ยื่นร่วม): answers are keyed by (questionItemID, personIndex).
      // Person 1 = ผู้มีเงินได้ (personIndex <= 1, the default for every legacy
      // single-person form); person 2 = คู่สมรส (personIndex >= 2). Each merged row
      // carries answerP1/answerP2; `answer` stays = person 1's answer so the
      // non-joint single-column render is byte-identical to before.
      // Answers match catalog items primarily by questionItemID. But a "Save" of the
      // question builder is a full-replace (RemoveRange+AddRange), so QuestionItemID
      // churns while the captured answers keep pointing at the OLD ids — matching by id
      // alone then shows every question TWICE (the fresh catalog item as "unanswered" +
      // the old answer as an orphan row). So we ALSO index answers by a stable natural
      // key (ruleCode, else fieldKey) and fall back to it when the id no longer matches.
      const natKey = (ruleCode, fieldKey) => {
        const r = String(ruleCode || '').trim().toLowerCase();
        if (r) return `r:${r}`;
        const f = String(fieldKey || '').trim().toLowerCase();
        return f ? `f:${f}` : '';
      };
      const p1ByItemId = new Map();
      const p2ByItemId = new Map();
      const p1ByNatKey = new Map();
      const p2ByNatKey = new Map();
      for (const a of this.answers) {
        const isP2 = (a.personIndex || 1) >= 2;
        // Last-wins (answers arrive ordered by AnswerID): a key shared by several rows
        // (GROUP_INPUT progressive fill, or an old + new answer for the same field after
        // a config re-save) collapses to the LATEST row — matching the pre-joint-filing
        // behavior exactly (byte-identical for non-joint).
        if (a.questionItemID != null) (isP2 ? p2ByItemId : p1ByItemId).set(a.questionItemID, a);
        const nk = natKey(a.ruleCode, a.fieldKey);
        if (nk) (isP2 ? p2ByNatKey : p1ByNatKey).set(nk, a);
      }
      const rows = [];
      const seen = new Set();
      const groups = (this.questionFull && Array.isArray(this.questionFull.groups))
        ? this.questionFull.groups.slice().sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
        : [];
      groups.forEach((g) => {
        const items = Array.isArray(g.items)
          ? g.items.slice().sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
          : [];
        items.forEach((item) => {
          if (item.itemKind === 'LAYOUT') return; // section headers / banners aren't answerable fields
          const nk = natKey(item.ruleCode, item.fieldKey);
          const answerP1 = p1ByItemId.get(item.questionItemID) || (nk && p1ByNatKey.get(nk)) || null;
          const answerP2 = p2ByItemId.get(item.questionItemID) || (nk && p2ByNatKey.get(nk)) || null;
          if (answerP1) seen.add(answerP1.answerID);
          if (answerP2) seen.add(answerP2.answerID);
          rows.push({
            key: `item-${item.questionItemID}`,
            questionTextTh: item.questionTextTh || item.label || '',
            groupCode: item.groupCode || g.groupCode || '',
            groupLabel: g.groupLabel || '',
            ruleCode: item.ruleCode || '',
            fieldKey: item.fieldKey || '',
            itemKind: item.itemKind || '',
            answer: answerP1,
            answerP1,
            answerP2,
          });
        });
      });
      // Captured answers whose item isn't in the current field list (e.g. the
      // item changed after the answer was captured) still need to show. Group by
      // questionItemID so a joint form's P1/P2 answers land in the same row.
      const orphanByKey = new Map();
      this.answers.forEach((a) => {
        if (seen.has(a.answerID)) return;
        const key = a.questionItemID != null ? `item-${a.questionItemID}` : `answer-${a.answerID}`;
        let row = orphanByKey.get(key);
        if (!row) {
          row = {
            key,
            questionTextTh: a.questionTextTh || '',
            groupCode: a.groupCode || '',
            groupLabel: a.groupLabel || '',
            ruleCode: a.ruleCode || '',
            fieldKey: a.fieldKey || '',
            itemKind: a.itemKind || '',
            answer: null,
            answerP1: null,
            answerP2: null,
          };
          orphanByKey.set(key, row);
          rows.push(row);
        }
        if ((a.personIndex || 1) >= 2) {
          if (!row.answerP2) row.answerP2 = a;
        } else if (!row.answerP1) {
          row.answerP1 = a;
        }
        row.answer = row.answerP1 || row.answerP2;
        if (!row.questionTextTh && a.questionTextTh) row.questionTextTh = a.questionTextTh;
      });
      return rows;
    },
    // Group fields by their groupCode so a long form reads as sections instead
    // of one flat wall — with the search filter applied first. Preserves
    // first-seen order; fields with no group fall into a single "ทั่วไป"
    // bucket (empty-string key). Each group carries the server-resolved
    // section label (from the question version) when present; the template
    // falls back to sectionLabel().
    filteredGroups() {
      const q = (this.answerSearch || '').trim().toLowerCase();
      const groups = new Map();
      for (const row of this.fields) {
        if (q && !this.answerMatches(row, q)) continue;
        const key = row.groupCode || '';
        if (!groups.has(key)) groups.set(key, { code: key, label: '', items: [] });
        const g = groups.get(key);
        g.items.push(row);
        if (!g.label && row.groupLabel) g.label = row.groupLabel;
      }
      return Array.from(groups.values());
    },
    // Chip label: total fields, or "shown / total" while a search is active.
    answersCountLabel() {
      const total = this.fields.length;
      if (!(this.answerSearch || '').trim()) return String(total);
      const shown = this.filteredGroups.reduce((n, g) => n + g.items.length, 0);
      return `${shown} / ${total}`;
    },
  },
  async created() {
    const formId = this.$route.params.formId;
    try {
      await loadFormDetail(formId);
    } catch (e) {
      // state carries detailError / detailErrorStatus; the template renders it.
      return;
    }
    await this.loadQuestionFields();
  },
  methods: {
    // Imported helper must be exposed on the instance so the template (_ctx)
    // can call it — Options API does not surface module-scope imports.
    formatDate,
    async reload() {
      try {
        await loadFormDetail(this.$route.params.formId);
      } catch (e) {
        // state carries the error.
        return;
      }
      await this.loadQuestionFields();
    },
    // Fetch the full field list for the form's question version (answered or
    // not). Failure/absence just leaves `questionFull` null — `fields` falls
    // back to answers-only in that case.
    async loadQuestionFields() {
      const versionId = this.form?.questionVersionID;
      if (!versionId) {
        this.questionFull = null;
        return;
      }
      this.fieldsLoading = true;
      try {
        this.questionFull = await taxQuestionApi.getFull(versionId);
      } catch (e) {
        this.questionFull = null;
      } finally {
        this.fieldsLoading = false;
      }
    },
    statusColor(status) {
      switch (status) {
        case 'InProgress': return 'warning';
        case 'Completed': return 'success';
        case 'Abandoned': return 'grey';
        default: return 'grey';
      }
    },
    // Section heading: prefer the project's defined label (ข้อมูลส่วนตัว /
    // รายได้ / …). groupLabel() echoes the raw UPPER code back when no label is
    // defined — detect that and humanize it so an unmapped code still reads
    // cleanly instead of showing "PERSONAL_INFO".
    sectionLabel(code) {
      const canonical = groupLabel(code);
      const upper = String(code || '').trim().toUpperCase();
      if (upper && canonical === upper) {
        return upper.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      }
      return canonical;
    },
    // A row the customer explicitly skipped (AnswerStatus='Skipped'): it HAS a row
    // (rawAnswer = what they said when skipping) but no value — rendered as a
    // "ข้ามไว้" chip, distinct from "ยังไม่ตอบ" (no row at all / not reached yet).
    isSkipped(item) {
      return !!item && item.answerStatus === 'Skipped';
    },
    // Format a single captured answer object to its display text.
    formatAnswer(item) {
      if (!item) return '';
      return (
        item.rawAnswer
        || item.normalizedValue
        || (Array.isArray(item.answerValues) && item.answerValues.length ? item.answerValues.join(', ') : '')
        || '—'
      );
    },
    // `row` is a merged field row (see `fields`) — its person-1 captured answer,
    // if any, lives under `row.answer` (the non-joint single-column value).
    answerText(row) {
      return this.formatAnswer(row.answer);
    },
    // Case-insensitive match across the fields a user would search by. In joint
    // mode both persons' answers are searchable (answerP1/answerP2).
    answerMatches(row, q) {
      const parts = [
        row.questionTextTh, this.formatAnswer(row.answerP1), this.formatAnswer(row.answerP2),
        row.ruleCode, row.fieldKey,
        row.itemKind, row.groupLabel, this.sectionLabel(row.groupCode),
      ];
      return parts.some((v) => v && String(v).toLowerCase().includes(q));
    },
  },
};
</script>

<style scoped>
/* ---- Record header ------------------------------------------------------ */
.rh-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.rh-title {
  font-size: 1.375rem;
  font-weight: 600;
  line-height: 1.2;
  margin: 0;
}
.rh-cif {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}
.rh-cif__label {
  font-size: 0.75rem;
  letter-spacing: 0.02em;
  color: rgba(0, 0, 0, 0.6);
}
.rh-status {
  font-weight: 600;
}
.rh-meta {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 16px;
}
.rh-timeline {
  display: inline-flex;
  align-items: center;
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.72);
}
.rh-progress {
  margin-top: 20px;
  max-width: 420px;
}
.rh-progress__label {
  display: flex;
  justify-content: space-between;
  font-size: 0.8125rem;
  color: rgba(0, 0, 0, 0.68);
  margin-bottom: 6px;
}

/* ---- Answers: search + legend ------------------------------------------- */
.answers-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px 20px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.answers-search {
  max-width: 360px;
  flex: 1 1 260px;
}
.answers-legend {
  margin: 0;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
}
.answers-legend strong {
  font-weight: 600;
  color: rgba(0, 0, 0, 0.78);
}

/* ---- Answers table (one table, group subheader rows) -------------------- */
.answers-scroll {
  max-height: 60vh;
  overflow: auto;
}
.answers-table :deep(th) {
  white-space: nowrap;
  font-weight: 600;
}
/* Column header sticks under scroll so it survives long forms. */
.answers-table :deep(thead th) {
  position: sticky;
  top: 0;
  z-index: 1;
  background: rgb(var(--v-theme-surface));
}
.th-jargon .th-info {
  margin-left: 2px;
  opacity: 0.55;
  cursor: help;
  vertical-align: text-top;
}
/* Section subheader row inside the table — tinted, left-aligned, full width. */
.group-row__cell {
  text-align: left;
  background: rgba(var(--v-theme-primary), 0.06);
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
.group-row__name {
  font-size: 0.9375rem;
  font-weight: 600;
}
.answer-q {
  min-width: 220px;
  max-width: 360px;
}
.answer-a {
  min-width: 160px;
  font-weight: 500;
}

/* ---- Technical details -------------------------------------------------- */
.tech-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px 24px;
  margin: 0;
}
.tech-item dt {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 2px;
}
.tech-item dd {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 500;
}

/* ---- Motion (transform-only so content is always visible) --------------- */
@keyframes rh-rise {
  from { transform: translateY(8px); }
  to { transform: none; }
}
.record-header,
.answers-card,
.tech-panels {
  animation: rh-rise 300ms cubic-bezier(0.22, 1, 0.36, 1) both;
}
.answers-card { animation-delay: 40ms; }
.tech-panels { animation-delay: 80ms; }
@media (prefers-reduced-motion: reduce) {
  .record-header,
  .answers-card,
  .tech-panels {
    animation: none;
  }
}
</style>
